import {
  type ClinicalImpressionAssessmentValueCode,
  type ClinicalImpressionPlanValueCode,
  GroupSecurity,
  getReferenceId,
} from '@quromedical/fhir-common';
import type { Note, StructuredNoteNode } from '@quromedical/models';
import { compact } from '@quromedical/utils';
import { Col } from 'components/base';
import { NoteSummaryGraph } from 'core/notes';
import { PatientHeaderSection } from 'core/patient';
import {
  Grid,
  getSelectedComments,
  isComment,
  type NestedComment,
  Section,
  SkeletonProvider,
  TextBox,
} from 'design-system';
import { router } from 'expo-router';
import { useMergedLatestVitals } from 'hooks/useMergedLatestVitals';
import { usePatient } from 'hooks/usePatient';
import { useIsBiggerThan } from 'hooks/useResponsive';
import { useUserSession } from 'hooks/useUserSession';
import { NotesApi, VitalsApi } from 'integration/aggregate';
import type React from 'react';
import { useCallback, useState } from 'react';
import { strings } from 'strings';

import {
  baseNoteOptions,
  NestedNoteForm,
  NoteForm,
  type SubmitData,
} from '../form';

// import { baseNoteOptions } from '../form/config';

interface NoteListHeaderProps {
  id: string;
  loading?: boolean;
  notes: Note[];
  search: string;
  setSearch: (query: string) => void;
  revalidate: () => void;
}

const api = new NotesApi();
const vitalApi = new VitalsApi();

const getCommentString = (node: NestedComment.Node) => {
  const comments = getSelectedComments(node);
  return comments.map((comment) => comment.display).join('. ');
};

const mapCommentToStructuredData = (
  node: NestedComment.Node,
): StructuredNoteNode => {
  if (isComment(node)) {
    return {
      type: 'comment',
      id: node.id,
      display: node.display,
      color: node.color,
      selected: node.selected,
      text: node.text,
    };
  }

  return {
    type: 'group',
    id: node.id,
    display: node.display,
    color: node.color,
    selected: node.selected,
    children: node.children.map(mapCommentToStructuredData),
  };
};

export const NoteListHeader: React.FC<NoteListHeaderProps> = ({
  id,
  loading = false,
  notes,
  search,
  setSearch,
  revalidate,
}) => {
  const latest = useMergedLatestVitals(id);
  const { organizations, practitioners } = usePatient(id);
  const isBig = useIsBiggerThan('md');

  const [lastRefreshed, setLastRefreshed] = useState(Date.now());
  const onRefresh = useCallback(() => {
    setLastRefreshed(Date.now());
    revalidate();
  }, [revalidate]);

  const onPrint = useCallback(() => {
    router.push({
      pathname: `/patient/${id}/notes/print-summary`,
    });
  }, [id]);

  const session = useUserSession();

  const viewPreviewNotes = session.hasAny([GroupSecurity.FeaturePreviewNotes]);
  const canCreateTask = session.hasAny([GroupSecurity.TaskCreate]);

  const handleSubmit = useCallback(
    async (data: SubmitData) => {
      await api.createNote({
        subject: id,
        code: data.code,
        locationCode: data.locationCode,
        media: data.media,
        vitals: latest,
        practitionersToNotify:
          practitioners?.filter((practitioner) =>
            data.practitionersToNotify.includes(
              getReferenceId(practitioner) || '',
            ),
          ) || [],
        note: {
          planCode: data.plan.children[0]
            ?.id as ClinicalImpressionPlanValueCode,
          assessmentCode: data.assessment.children[0]
            ?.id as ClinicalImpressionAssessmentValueCode,
          subjective: getCommentString(data.subjective),
          assessment: getCommentString(data.assessment),
          objective: getCommentString(data.objective),
          plan: getCommentString(data.plan),
        },
        structuredContent: {
          type: 'nested-comment-nodes',
          data: compact([
            data.subjective,
            data.objective,
            data.assessment,
            data.plan,
            data.clinicalOperations,
          ]).map(mapCommentToStructuredData),
        },
      });

      revalidate();
    },
    [id, latest, practitioners, revalidate],
  );

  return (
    <SkeletonProvider loading={loading}>
      <Col key={lastRefreshed}>
        <PatientHeaderSection
          id={id}
          action={{ icon: 'print', onPress: onPrint }}
        />

        <Section isVisible={viewPreviewNotes}>
          <NestedNoteForm
            id={id}
            onSubmit={handleSubmit}
            options={baseNoteOptions}
            generalPractitioners={practitioners}
          />
        </Section>

        <Section isVisible={!viewPreviewNotes}>
          <NoteForm
            canCreateTask={canCreateTask}
            onRefresh={onRefresh}
            latest={latest}
            generalPractitioners={practitioners}
            organizationTeams={organizations}
            id={id}
          />
        </Section>

        <Section>
          <Grid sm={1} xl={2}>
            <TextBox
              onChange={setSearch}
              value={search}
              placeholder={strings.TextBoxPlaceholderSearch}
            />
          </Grid>
        </Section>
        <Section isVisible={isBig} hasBottomMargin>
          <NoteSummaryGraph id={id} notes={notes} vitals={vitalApi} />
        </Section>
      </Col>
    </SkeletonProvider>
  );
};
